import type { FC, PropsWithChildren, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'next-i18next/pages'
import { Button } from '@corratech/pylot-ui'
import { Cross, Heart, Trash, Warning } from '@components/icons'
import cn from 'classnames'
import s from './Confirm.module.scss'

export type ConfirmBoxIcon = 'heart' | 'trash' | 'warning'

export type ConfirmBoxType = PropsWithChildren<{
    show: boolean
    closeConfirm: () => void
    handleAction?: () => void | Promise<void>
    handleTertiaryAction?: () => void
    footer?: boolean
    className?: string
    variant?: 'default' | 'cart'
    /** Icono superior izquierdo (variante cart). */
    icon?: ConfirmBoxIcon
    /** Título en negrita uppercase (variante cart). */
    title?: string
    /** Texto de apoyo debajo del título. */
    description?: string
    confirmString?: string
    cancelString?: string
    tertiaryString?: string
    confirmLoading?: boolean
    centered?: boolean
    cancelVariant?: 'primary' | 'secondary'
    confirmVariant?: 'primary' | 'secondary'
    tertiaryVariant?: 'primary' | 'secondary'
}>

const iconMap: Record<ConfirmBoxIcon, ReactNode> = {
    heart: <Heart className={s['header-icon-svg']} aria-hidden />,
    trash: <Trash className={s['header-icon-svg']} aria-hidden />,
    warning: <Warning className={s['header-icon-svg']} aria-hidden />
}

export const ConfirmBox: FC<ConfirmBoxType> = ({
    show,
    closeConfirm,
    handleAction,
    handleTertiaryAction,
    children,
    footer = true,
    className,
    variant = 'default',
    icon,
    title,
    description,
    confirmString,
    cancelString,
    tertiaryString,
    confirmLoading = false,
    centered = false,
    cancelVariant = 'primary',
    confirmVariant = 'secondary',
    tertiaryVariant = 'secondary'
}) => {
    const { t } = useTranslation('common')
    const isCart = variant === 'cart'

    if (!show || typeof document === 'undefined') return null

    const cancelLabel = (cancelString || t('Cancel')).toUpperCase()
    const confirmLabel = (confirmString || t('Ok')).toUpperCase()
    const tertiaryLabel = tertiaryString?.toUpperCase()

    const modal = (
        <div
            className={cn(
                s['confirm-modal-box'],
                isCart && s['confirm-modal-box--cart'],
                className
            )}
        >
            <div
                className={cn(s['confirm-overlay'], 'confirm-overlay')}
                aria-hidden="true"
                onClick={closeConfirm}
            />
            <div
                className={cn(
                    s['confirm-modal-inner'],
                    'confirm-modal-inner',
                    'animate-fade-in-down',
                    centered && s.centered
                )}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? 'confirm-box-title' : undefined}
            >
                {!isCart && (
                    <div className={cn(s['modal-header'], 'modal-header')}>
                        <span
                            tabIndex={0}
                            role="button"
                            onClick={closeConfirm}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    closeConfirm()
                                }
                            }}
                            className={s['close-btn']}
                            aria-label={cancelLabel}
                        >
                            <Cross />
                        </span>
                    </div>
                )}

                <div className={cn(s['modal-content'], 'modal-content')}>
                    {(icon || title) && (
                        <div className={s['header-row']}>
                            {isCart && icon && (
                                <div className={s['header-icon']}>
                                    {iconMap[icon]}
                                </div>
                            )}
                            {title && (
                                <h2
                                    id="confirm-box-title"
                                    className={s['title']}
                                >
                                    {title}
                                </h2>
                            )}
                        </div>
                    )}
                    {description && (
                        <p className={s['description']}>{description}</p>
                    )}
                    {children}
                </div>

                {footer && (
                    <div className={cn(s['modal-footer'], 'modal-footer')}>
                        {isCart ? (
                            <>
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={handleAction}
                                    className={cn(s['confirm'], s['btn-full'])}
                                    width="100%"
                                    loading={confirmLoading}
                                    disabled={confirmLoading}
                                >
                                    {confirmLabel}
                                </Button>
                                {tertiaryString && handleTertiaryAction && (
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={handleTertiaryAction}
                                        className={cn(
                                            s['tertiary'],
                                            s['btn-full']
                                        )}
                                        width="100%"
                                        disabled={confirmLoading}
                                    >
                                        {tertiaryLabel}
                                    </Button>
                                )}
                                <button
                                    type="button"
                                    className={s['cancel-link']}
                                    onClick={closeConfirm}
                                    disabled={confirmLoading}
                                >
                                    {cancelLabel}
                                </button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    variant={cancelVariant}
                                    onClick={closeConfirm}
                                    className={s['confirm']}
                                    disabled={confirmLoading}
                                >
                                    {cancelString || t('Cancel')}
                                </Button>
                                <Button
                                    type="button"
                                    variant={confirmVariant}
                                    onClick={handleAction}
                                    className={s['delete']}
                                    loading={confirmLoading}
                                    disabled={confirmLoading}
                                >
                                    {confirmString || t('Ok')}
                                </Button>
                                {tertiaryString && handleTertiaryAction && (
                                    <Button
                                        type="button"
                                        variant={tertiaryVariant}
                                        onClick={handleTertiaryAction}
                                        className={s['tertiary']}
                                        disabled={confirmLoading}
                                    >
                                        {tertiaryString}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    )

    return createPortal(modal, document.body)
}
